import { useModal } from "@newsela/angelou";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";
import { useEffect, useMemo, useRef, useState } from "react";

import api, { ApiCatalog } from "@/api";
import { VIEW_PAGE_ASSIGN_GRAPHIC_ORGANIZERS_FEATURE } from "@/assignments/constants/experiments";
import useGetExportables from "@/assignments/useGetExportables";
import { GRAPHIC_ORGANIZER_PARAM } from "@/components/AIComponents/ExportablesModal";
import {
  TEMPLATE_REGISTRY,
  type TemplateDefinition,
} from "@/components/AIComponents/ExportablesModal/templates/registry";
import { buildTemplateList } from "@/components/AIComponents/ExportablesModal/utils";
import useHasFeature from "@/features/useHasFeature";
import { isSupportedEnglishContent } from "@/utils/generatedContent";
import contentTypes from "@/view/constants/contentTypes";
import useStudentPreviewContext from "@/view/utils/useStudentPreviewContext";
import { ExportablesContext } from "./useExportablesContext";

import type {
  GraphicOrganizerActivity,
  GraphicOrganizersActivities,
} from "@/types/models";
import type {
  ExportablesDataTypes,
  ExportablesFormData,
  FactOpinionSorter,
} from "@/types/models/Exportables.interface";
import type {
  ExportablesContextValue,
  ExportablesModalCommonProps,
} from "./ExportablesContextProvider.types";

interface ExportablesContextProps extends ExportablesModalCommonProps {
  children: React.ReactNode;
  isStudent: boolean;
}

const ExportablesContextProvider = ({
  children,
  content,
  contentMetadata = [],
  currentLevel,
  isStudent,
  levelId,
}: ExportablesContextProps) => {
  const contentId = content?.id || "";
  const contentType = content?.content_type ?? contentTypes.ARTICLE;

  const router = useRouter();
  const { pathname, query } = router;
  const { selectedAssignmentId } = query as {
    selectedAssignmentId?: string | undefined;
  };

  const { isStudentPreviewOn } = useStudentPreviewContext();

  const [modalProps, modalSetActive, modalTriggerRef] = useModal(false);
  const [formData, setFormData] = useState<ExportablesFormData>({});
  const [selectedExportable, setSelectedExportable] =
    useState<TemplateDefinition>(TEMPLATE_REGISTRY.AnticipationGuide);
  // State to track which templates are selected for assignment
  const [templatesToAssign, setTemplatesToAssign] = useState<
    TemplateDefinition<ExportablesDataTypes>[]
  >([]);
  // State to track assigned templates
  const [assignedTemplates, setAssignedTemplates] = useState<
    TemplateDefinition<ExportablesDataTypes>[]
  >([]);

  const [showAssignmentWarning, setShowAssignmentWarning] = useState(false);
  const [renderOnMobile, setRenderOnMobile] = useState(false);
  const [
    isAssignableGraphicOrganizersActivityChecked,
    setIsAssignableGraphicOrganizersActivityChecked,
  ] = useState(true);

  const {
    data: fetchedGraphicOrganizerResponseData,
    isLoading: isLoadingGraphicOrganizerResponseData,
  } = useQuery<GraphicOrganizersActivities>({
    queryKey: [ApiCatalog.graphicOrganizer.key, contentId],
    queryFn: async () =>
      await api().get(ApiCatalog.graphicOrganizer.endpoint, {
        queryParams: {
          content_id: contentId,
          assignment_id: Array.isArray(selectedAssignmentId)
            ? selectedAssignmentId[0]
            : selectedAssignmentId,
          include_responses: isStudent && Boolean(selectedAssignmentId),
        },
      }),
    enabled: !isStudent || (isStudent && Boolean(selectedAssignmentId)),
  });

  const isContentSupported = isSupportedEnglishContent({
    contentType,
    currentLevel,
    contentMetadata,
  });

  const [isInAssignGraphicOrganizersExperiment] = useHasFeature(
    VIEW_PAGE_ASSIGN_GRAPHIC_ORGANIZERS_FEATURE,
  );

  const { data: exportablesMetadata, loading: exportablesLoading } =
    useGetExportables({
      articleId: contentId,
      levelId,
      skip: !isContentSupported,
    }) || {};

  const templateList = useMemo(() => {
    return buildTemplateList({
      isStudent,
      isStudentPreviewOn,
      fetchedGraphicOrganizerResponseData,
      exportablesMetadata,
    });
  }, [
    isStudent,
    isStudentPreviewOn,
    fetchedGraphicOrganizerResponseData,
    exportablesMetadata,
  ]);

  const isLoading =
    isLoadingGraphicOrganizerResponseData ||
    exportablesLoading ||
    exportablesMetadata === null;

  const handleModalClose = () => {
    modalSetActive(false);

    const newQuery = { ...query };
    delete newQuery[GRAPHIC_ORGANIZER_PARAM];

    router.replace({ pathname, query: newQuery }, undefined, { shallow: true });
  };

  const handleTemplateSelection = (
    template?: TemplateDefinition<ExportablesDataTypes>,
  ) => {
    const templateToSelect = template || templateList[0];

    if (!templateToSelect) return;

    setSelectedExportable(templateToSelect);

    const newQuery = {
      ...query,
      [GRAPHIC_ORGANIZER_PARAM]: templateToSelect.seti_identifier,
    };

    router.replace({ pathname, query: newQuery }, undefined, { shallow: true });
  };

  const initializedTemplates = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!fetchedGraphicOrganizerResponseData?.graphic_organizers) return;

    const fetchedAssignedTemplates =
      fetchedGraphicOrganizerResponseData.graphic_organizers
        .map((graphicOrganizer) => {
          const setiIdentifier =
            graphicOrganizer.graphic_organizer_type.replace(
              "graphic_organizer_",
              "",
            );

          return templateList.find(
            (template) => template.seti_identifier === setiIdentifier,
          );
        })
        .filter(Boolean) as TemplateDefinition<ExportablesDataTypes>[];

    setAssignedTemplates(fetchedAssignedTemplates);
  }, [
    JSON.stringify(fetchedGraphicOrganizerResponseData?.graphic_organizers),
    JSON.stringify(templateList),
  ]);

  useEffect(() => {
    if (!selectedExportable || !exportablesMetadata) return;

    // If the modal is closed, reset initialized templates
    if (!modalProps.isActive) {
      initializedTemplates.current.clear();

      // Only keep formData for assigned templates
      const preservedFormData: Record<string, Record<string, any>> = {};
      assignedTemplates.forEach((template) => {
        const templateFormData = formData[template.seti_identifier];
        if (templateFormData) {
          preservedFormData[template.seti_identifier] = templateFormData;
        }
      });

      setFormData(preservedFormData);
    }
  }, [modalProps?.isActive]);

  useEffect(() => {
    if (!selectedExportable || !exportablesMetadata || !modalProps?.isActive)
      return;

    const templateSetiIdentifier = selectedExportable.seti_identifier;

    // If we've already initialized this template, don't do it again
    if (initializedTemplates.current.has(templateSetiIdentifier)) {
      return;
    }

    // Check if this template is in assignedTemplates (has saved data)
    const isAssigned = assignedTemplates.some(
      (t) => t.seti_identifier === templateSetiIdentifier,
    );

    // If assigned and already has formData, keep it
    if (isAssigned && formData[templateSetiIdentifier]) {
      initializedTemplates.current.add(templateSetiIdentifier);
      return;
    }

    const graphicOrganizerActivity =
      fetchedGraphicOrganizerResponseData?.graphic_organizers?.find(
        (templateData: GraphicOrganizerActivity) =>
          templateData.graphic_organizer_type.includes(
            selectedExportable.seti_identifier,
          ),
      );

    // Build base template data from custom data or generated metadata
    const customTemplateData = graphicOrganizerActivity?.data;
    const hasCustomData =
      customTemplateData && Object.keys(customTemplateData).length > 0;
    const baseTemplateData = hasCustomData
      ? customTemplateData
      : exportablesMetadata?.[selectedExportable.seti_identifier] || {};

    // Get student response data if available
    const studentResponseData =
      graphicOrganizerActivity?.responses &&
      graphicOrganizerActivity.responses.length > 0
        ? // When this endpoint is accessed by a student, only one response is included in the array
          graphicOrganizerActivity.responses[0]?.data
        : null;

    // Merge student responses over base template data
    const templateData = studentResponseData
      ? { ...baseTemplateData, ...studentResponseData }
      : baseTemplateData;

    // Clear optional fields for primary_source_analysis when loading from SETI
    // (not custom data) and only for teachers. These fields will be controlled
    // by a toggle in the future (LESS-5024).
    const shouldClearOptionalFields = !isStudent && !hasCustomData;

    let finalTemplateData = templateData;
    if (shouldClearOptionalFields) {
      if (templateSetiIdentifier === "primary_source_analysis") {
        finalTemplateData = {
          ...templateData,
          intended_audience: "",
          conclusion: "",
          evidence_of_bias: "",
          excluded_topics: "",
          reliability: "",
          world_events: "",
        };
      } else if (templateSetiIdentifier === "fact_opinion") {
        const statements = (templateData as FactOpinionSorter).statements ?? [];
        finalTemplateData = {
          ...templateData,
          statements: statements.map(({ statement }) => ({
            statement,
            classification: "",
            explanation: "",
          })),
        };
      }
    }

    setFormData((prevFormData) => ({
      ...prevFormData,
      [selectedExportable.seti_identifier]: {
        ...finalTemplateData,
        articleShortTitle: currentLevel?.short_title,
      },
    }));

    // Mark this template as initialized
    initializedTemplates.current.add(selectedExportable.seti_identifier);
  }, [
    currentLevel?.short_title,
    selectedExportable?.seti_identifier,
    JSON.stringify(exportablesMetadata),
    modalProps?.isActive,
    JSON.stringify(assignedTemplates),
    JSON.stringify(formData),
    JSON.stringify(fetchedGraphicOrganizerResponseData?.graphic_organizers),
  ]);

  useEffect(() => {
    const graphicOrganizerParam = query[GRAPHIC_ORGANIZER_PARAM];

    if (graphicOrganizerParam && typeof graphicOrganizerParam === "string") {
      const template = templateList.find(
        (t) => t.seti_identifier === graphicOrganizerParam,
      );

      if (template) {
        setSelectedExportable(template);
        modalSetActive(true);
      }
    }
  }, [query, modalSetActive]);

  // Initialize templatesToAssign with assignedTemplates when modal opens
  useEffect(() => {
    if (modalProps?.isActive) {
      setTemplatesToAssign(assignedTemplates);
    }
  }, [modalProps.isActive, assignedTemplates]);

  const contextValue: ExportablesContextValue = {
    assignedTemplates,
    content,
    contentMetadata,
    currentLevel,
    exportablesMetadata,
    formData,
    handleModalClose,
    handleTemplateSelection,
    isContentSupported,
    isInAssignGraphicOrganizersExperiment,
    isAssignableGraphicOrganizersActivityChecked,
    isLoading,
    isStudent,
    levelId,
    modalProps,
    modalSetActive,
    modalTriggerRef,
    selectedAssignmentId,
    selectedExportable,
    setAssignedTemplates,
    setFormData,
    setIsAssignableGraphicOrganizersActivityChecked,
    setSelectedExportable,
    setShowAssignmentWarning,
    setTemplatesToAssign,
    setRenderOnMobile,
    showAssignmentWarning,
    renderOnMobile,
    templateList,
    templatesToAssign,
  };

  return (
    <ExportablesContext.Provider value={contextValue}>
      {children}
    </ExportablesContext.Provider>
  );
};

export default ExportablesContextProvider;
